import whoopsImg from './images/whoops.svg';
import unpluggedImg from './images/unplugged.svg';
import whoopsBoxImg from './images/whoops-box.svg';
import welcomeLady from './images/welcome_lady.svg';
import timerImg from './images/timer.svg';
import openBoxImg from './images/open-box.svg';
import customerService from './images/customer_support.svg';
import http from 'lc-http';
import { heapTrackErrors } from '@shared_modules/baui-heap-tracking';
import ERROR_CODES from '@shared_modules/common-properties/errors';
import React from 'react';

const goToMC = {
  buttonText: 'Go to Member Center',
  buttonHref: '/account/services/dashboard',
};

const titleAndImg = {
  title: 'Whoops!',
  iconImg: whoopsBoxImg,
};

const COMMON_ERROR_OBJECT = {
  buttonClasses: 'PlatformError-button--no-space',
  buttonHref: '/account/services/dashboard',
  buttonText: 'Back to Your Account',
  containerClasses: 'PlatformError-page--topup',
  description: ERROR_CODES.CHARGED_OFF_PRIOR_LOAN,
  iconImg: customerService,
  title: 'Please contact us.',
};

const PL2BT = {
  DEFAULT: {
    title: 'Whoops!',
    description: 'Something went wrong,\nplease come back later.',
    iconImg: whoopsImg,
    ...goToMC,
  },
  UNEXPECTED_ERROR: {
    title: 'Whoops!',
    description: 'Something went wrong,\nplease come back later.',
    iconImg: unpluggedImg,
    buttonText: 'Refresh This Page',
    buttonAction: () => {
      window?.location.reload();
    },
  },
  SESSION_MISMATCH: {
    title: 'Whoops!',
    description: 'Someone else is logged in',
    iconImg: unpluggedImg,
    buttonText: 'Sign Out to Login',
    buttonAction: async () => {
      try {
        await http.get('/auth/logout');
        window?.location.reload();
      } catch (e) {
        heapTrackErrors([{ error: e, message: 'Logout failed' }]);
      }
    },
  },
  PSSID_MISMATCH: {
    title: 'Whoops!',
    description: 'Looks like you already have a loan\napplication in progress.',
    iconImg: whoopsImg,
    ...goToMC,
  },
};

const ILOC = {
  DEFAULT: {
    title: 'Whoops!',
    description: 'Something went wrong, please come\nback later.',
    subText: 'Please refresh the page.',
    iconImg: unpluggedImg,
    heapEventView: 'Origination_CleanSweep_LandingPage_InvitationExpired_Error',
  },
  PRODUCT_NOT_SUPPORTED: {
    header: 'thank you for your interest',
    title: 'CleanSweep is no longer available.',
    description: (
      <>
        We’re sorry. LendingClub no longer offers the CleanSweep™ line of credit. We appreciate your understanding and
        are focused on developing new products that meet your financial needs. <br /> <br />
        We'll keep you informed about upcoming member offers. In the meantime, visit{' '}
        <a href="/" target="_blank">
          lendingclub.com
        </a>{' '}
        to explore our current products.
      </>
    ),
    buttonClasses: 'PlatformError-button--full-width',
    buttonVariant: 'primary',
    buttonText: 'Visit LendingClub.com',
    buttonHref: '/',
    buttonHrefEventName: 'Origination_CleanSweep_PI1_ErrorGoBackToLC',
    containerClasses: 'PlatformError-page--product-not-supported',
    heapEventView: 'Origination_CleanSweep_LandingPage_CleansweepNoLongerAvailable_Error',
  },
  TOKEN_EXPIRED: {
    title: 'Whoops!',
    description: 'Looks like your offer has\nexpired.',
    iconImg: timerImg,
    buttonText: 'Sign In to Member Center',
    buttonHref: '/account/services/dashboard',
    buttonHrefEventName: 'Origination_CleanSweep_PI1_ErrorGoBackToMC',
    heapEventView: 'Origination_CleanSweep_LandingPage_InvitationExpired_OfferExpired',
  },
  TOKEN_NOT_FOUND: {
    title: 'Whoops!',
    description: 'Something went wrong, please come\nback later.',
    iconImg: unpluggedImg,
    ...goToMC,
    buttonHrefEventName: 'Origination_CleanSweep_PI1_ErrorGoBackToMC',
    heapEventView: 'Origination_CleanSweep_LandingPage_InvitationExpired_Error',
  },
  MALFORMED: {
    title: 'Whoops!',
    description: 'Something went wrong, please come\nback later.',
    iconImg: unpluggedImg,
    ...goToMC,
    buttonHrefEventName: 'Origination_CleanSweep_PI1_ErrorGoBackToMC',
    heapEventView: 'Origination_CleanSweep_LandingPage_InvitationExpired_Error',
  },
  TOKEN_USED: {
    title: (
      <>
        Unfortunately, you are not eligible for <span className="PlatformError-trade">CleanSweep</span> at this time
      </>
    ),
    iconImg: openBoxImg,
    buttonClasses: 'PlatformError-button--no-space',
    ...goToMC,
    buttonHrefEventName: 'Origination_CleanSweep_PI1_ErrorGoBackToMC',
  },
  PSSID_MISMATCH: {
    title: (
      <>
        Unfortunately, <span className="PlatformError-trade">CleanSweep</span> is not available to you
      </>
    ),
    description: 'Because you already have a personal loan\napplication in progress.',
    iconImg: openBoxImg,
    buttonClasses: 'PlatformError-button--no-space',
    ...goToMC,
    buttonHrefEventName: 'Origination_CleanSweep_PI1_ErrorGoBackToMC',
    heapEventView: 'Origination_CleanSweep_LandingPage_InvitationExpired_LoanInProgress',
  },
  USER_INELIGIBLE: {
    title: (
      <>
        Unfortunately, you are not eligible for <span className="PlatformError-trade">CleanSweep</span> at this time
      </>
    ),
    iconImg: openBoxImg,
    buttonClasses: 'PlatformError-button--no-space',
    ...goToMC,
    buttonHrefEventName: 'Origination_CleanSweep_PI1_ErrorGoBackToMC',
    heapEventView: 'Origination_CleanSweep_LandingPage_Ineligible',
  },
  SESSION_MISMATCH: {
    title: 'Whoops!',
    description: 'Something went wrong, please come\nback later.',
    iconImg: unpluggedImg,
    ...goToMC,
    buttonHrefEventName: 'Origination_CleanSweep_PI1_ErrorGoBackToMC',
    heapEventView: 'Origination_CleanSweep_LandingPage_InvitationExpired_Error',
  },
};

const TOPUP = {
  DEFAULT: {
    title: 'Whoops!',
    description: 'Something went wrong, please come\nback later.',
    subText: 'Please refresh the page.',
    iconImg: unpluggedImg,
  },
  TOKEN_EXPIRED: {
    title: 'Whoops!',
    description: 'Looks like your offer has\nexpired.',
    iconImg: timerImg,
    ...goToMC,
    buttonHrefEventName: 'Origination_TopUp_PI1_ErrorGoBackToMC',
  },
  TOKEN_NOT_FOUND: {
    title: 'Whoops!',
    description: 'Something went wrong, please come\nback later.',
    iconImg: unpluggedImg,
    ...goToMC,
    buttonHrefEventName: 'Origination_TopUp_PI1_ErrorGoBackToMC',
  },
  MALFORMED: {
    title: 'Whoops!',
    description: 'Something went wrong, please come\nback later.',
    iconImg: unpluggedImg,
    ...goToMC,
    buttonHrefEventName: 'Origination_TopUp_PI1_ErrorGoBackToMC',
  },
  TOKEN_USED: {
    title: (
      <>
        Unfortunately, you are not eligible for <span className="PlatformError-trade">TopUp Loan</span> at this time
      </>
    ),
    iconImg: openBoxImg,
    buttonClasses: 'PlatformError-button--no-space',
    ...goToMC,
    buttonHrefEventName: 'Origination_TopUp_PI1_ErrorGoBackToMC',
  },
  PSSID_MISMATCH: {
    title: (
      <>
        Unfortunately, <span className="PlatformError-trade">TopUp Loan</span> is not available to you
      </>
    ),
    description: 'Because you already have a personal loan\napplication in progress.',
    iconImg: openBoxImg,
    buttonClasses: 'PlatformError-button--no-space',
    ...goToMC,
    buttonHrefEventName: 'Origination_TopUp_PI1_ErrorGoBackToMC',
  },
  USER_INELIGIBLE: {
    title: (
      <>
        Unfortunately, you are not eligible for <span className="PlatformError-trade">TopUp Loan</span> at this time
      </>
    ),
    iconImg: openBoxImg,
    buttonClasses: 'PlatformError-button--no-space',
    ...goToMC,
    buttonHrefEventName: 'Origination_TopUp_PI1_ErrorGoBackToMC',
  },
  SESSION_MISMATCH: {
    title: 'Whoops!',
    description: 'Something went wrong, please come\nback later.',
    iconImg: unpluggedImg,
    ...goToMC,
    buttonHrefEventName: 'Origination_TopUp_PI1_ErrorGoBackToMC',
  },
  EXCEED_MAX_OUTSTANDING_PRINCIPAL: {
    ...COMMON_ERROR_OBJECT,
    description: ERROR_CODES.EXCEED_MAX_OUTSTANDING_PRINCIPAL,
  },
  CHARGED_OFF_PRIOR_LOAN: COMMON_ERROR_OBJECT,
  DELINQUENT_CURRENT_LOAN: COMMON_ERROR_OBJECT,
  ENROLLED_IN_HARDSHIP_PLAN: COMMON_ERROR_OBJECT,
  INSUFFICIENT_ON_TIME_PAYMENTS_ON_EXISTING_LOANS: COMMON_ERROR_OBJECT,
};

const eventName = 'Token_Validation_Message_Page_Submit';
const PL_PRE_APPROVAL = {
  DEFAULT: {
    ...titleAndImg,
    description: 'We couldn’t find your pre-approved offer.',
    subText: 'Please try again later.',
    ...goToMC,
  },
  SESSION_MISMATCH: {
    ...titleAndImg,
    description:
      'Something went wrong, please make sure the account you are using for login is the same account where you got your pre-approved offer.',
    buttonHrefEventName: eventName,
    ...goToMC,
  },
  EXPIRED: {
    title: 'Whoops!',
    description: 'Looks like your pre-approved offer has expired.',
    subText: 'You can start a new loan application from Member Center.',
    iconImg: timerImg,
    buttonHrefEventName: eventName,
    ...goToMC,
  },
  INACTIVE: {
    ...titleAndImg,
    description:
      'Unfortunately, your pre-approved offer is no longer valid. You can start a new loan application from Member Center.',
    buttonClasses: 'PlatformError-button--no-space',
    buttonHrefEventName: eventName,
    ...goToMC,
  },
  CLOSED: {
    ...titleAndImg,
    description: 'Looks like you already submitted your application in response to this pre-approved offer.',
    buttonClasses: 'PlatformError-button--no-space',
    buttonHrefEventName: eventName,
    ...goToMC,
  },
  TOKEN_NOT_FOUND: {
    ...titleAndImg,
    description: 'We couldn’t find your pre-approved offer.',
    subText: 'You can start a new application from Member Center.',
    buttonHrefEventName: eventName,
    ...goToMC,
  },
  MALFORMED: {
    ...titleAndImg,
    description: 'We couldn’t find your pre-approved offer.',
    subText: 'You can start a new application from Member Center.',
    buttonHrefEventName: eventName,
    ...goToMC,
  },
  APP_IN_PROG_RETURN: {
    title: 'Welcome Back!',
    description: 'Thank you for checking your pre-approved offer.',
    subText: 'We are redirecting you to your application in progress.',
    iconImg: welcomeLady,
    buttonClasses: 'PlatformError-button--no-space',
    buttonHrefEventName: eventName,
  },
  APP_IN_PROG_OTHER: {
    title: 'Whoops!',
    description: 'Looks like you already have another application in progress.',
    subText: (
      <>
        <div>If you want to cancel that application, call our Customer Service team to this number</div>
        <div>
          <a href="tel:8885963157">888-596-3157</a>.
        </div>
        <br />
        <div>
          After cancelling, you can try to access your pre-approved offer again. Otherwise, you can continue with your
          existing application by clicking below.
        </div>
      </>
    ),
    iconImg: openBoxImg,
    buttonClasses: 'PlatformError-button',
    buttonText: 'Go to My Application in Progress',
    buttonHref: '/apply/personal/identity',
    buttonHrefEventName: eventName,
  },
};

const MAIN = {
  LOAN_APP_IN_PROGRESS: {
    title: 'Whoops!',
    description: 'Looks like you already have another application in progress.',
    subText: (
      <>
        <div>If you want to cancel that application, call our Customer Service team at this number:</div>
        <div>
          <a href="tel:8885963157">888-596-3157</a>.
        </div>
        <br />
        <div>
          After cancelling, you can begin a new application. Otherwise, you can continue with your existing application
          by clicking below.
        </div>
      </>
    ),
    iconImg: openBoxImg,
    buttonClasses: 'PlatformError-button',
    buttonText: 'Go to My Application in Progress',
    buttonHref: '/apply/personal/identity',
    buttonHrefEventName: 'Originations_Error_Page_Submit',
    buttonVariant: 'secondary',
    productType: 'PL',
  },
  REJECTED_BORROWER: {
    title: 'Whoops!',
    subText: (
      <>
        <p>It looks like you applied with us recently, and we couldn’t approve your application.</p>
        <br />
        <p>You are welcome to apply again after 30 days from your most recent application.</p>
      </>
    ),
    iconImg: whoopsImg,
    buttonHrefEventName: 'Originations_Error_Page_Submit',
    productType: 'PL',
    ...goToMC,
  },
};

const errorsMapping = {
  PL2BT,
  ILOC,
  TOPUP,
  PL_PRE_APPROVAL,
  MAIN,
};

export default errorsMapping;

